import apiFetch from "@/app/auth/apiFetch";
import type { NavigateFunction } from "react-router-dom";

const API_URL = import.meta.env.VITE_API_URL;

/**
 * Bibliotheque de messages pre-rediges standard de la plateforme.
 * Backend : `/api/admin/message-templates` (backoffice, admin-gated).
 *
 * C'est la bibliotheque heritee par toute nouvelle organisation qui n'a pas
 * edite la sienne. Une modification prend effet a la creation de la PROCHAINE
 * campagne — les campagnes existantes ont leur propre copie.
 */

export interface MessageTemplate {
  id: number;
  key: string;
  /** Prefixe de `key` = scenario de contact, derive cote serveur. */
  scenario: string;
  channel: string | null;
  language: string | null;
  content: string;
  position: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface MessageScenario {
  prefix: string;
  label: string;
  description: string;
}

export interface MessageTemplateCreate {
  key: string;
  channel?: string | null;
  language?: string | null;
  content: string;
  position?: number;
  is_active?: boolean;
}

export interface MessageTemplateUpdate {
  key?: string | null;
  channel?: string | null;
  language?: string | null;
  content?: string | null;
  position?: number | null;
  is_active?: boolean | null;
}

async function readJson<T>(res: Response | undefined): Promise<T> {
  if (!res) throw new Error("Pas de reponse du serveur.");
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const body = await res.json();
      if (body?.detail) {
        detail =
          typeof body.detail === "string"
            ? body.detail
            : JSON.stringify(body.detail);
      }
    } catch {
      // ignore
    }
    throw new Error(detail);
  }
  return (await res.json()) as T;
}

export async function listMessageTemplates(
  navigate: NavigateFunction,
): Promise<MessageTemplate[]> {
  const res = await apiFetch(
    `${API_URL}/admin/message-templates`,
    { method: "GET" },
    navigate,
  );
  return readJson<MessageTemplate[]>(res);
}

export async function listMessageScenarios(
  navigate: NavigateFunction,
): Promise<MessageScenario[]> {
  const res = await apiFetch(
    `${API_URL}/admin/message-templates/scenarios`,
    { method: "GET" },
    navigate,
  );
  return readJson<MessageScenario[]>(res);
}

export async function createMessageTemplate(
  payload: MessageTemplateCreate,
  navigate: NavigateFunction,
): Promise<MessageTemplate> {
  const res = await apiFetch(
    `${API_URL}/admin/message-templates`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
    navigate,
  );
  return readJson<MessageTemplate>(res);
}

export async function updateMessageTemplate(
  id: number,
  payload: MessageTemplateUpdate,
  navigate: NavigateFunction,
): Promise<MessageTemplate> {
  const res = await apiFetch(
    `${API_URL}/admin/message-templates/${id}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
    navigate,
  );
  return readJson<MessageTemplate>(res);
}

export async function deleteMessageTemplate(
  id: number,
  navigate: NavigateFunction,
): Promise<void> {
  const res = await apiFetch(
    `${API_URL}/admin/message-templates/${id}`,
    { method: "DELETE" },
    navigate,
  );
  if (!res?.ok) {
    throw new Error(`HTTP ${res?.status ?? "?"}`);
  }
}

/**
 * Export CSV. Le blob est declenche cote navigateur : `apiFetch` porte le
 * Bearer, donc un `<a href>` direct renverrait un 401.
 */
export async function exportMessageTemplatesCsv(
  navigate: NavigateFunction,
): Promise<Blob> {
  const res = await apiFetch(
    `${API_URL}/admin/message-templates/export`,
    { method: "GET" },
    navigate,
  );
  if (!res?.ok) {
    throw new Error(`HTTP ${res?.status ?? "?"}`);
  }
  return res.blob();
}

/** Import CSV — remplace TOUTE la bibliotheque (validate-all puis replace). */
export async function importMessageTemplatesCsv(
  file: File,
  navigate: NavigateFunction,
): Promise<MessageTemplate[]> {
  const body = new FormData();
  body.append("file", file);
  // Pas de Content-Type manuel : le navigateur doit poser le boundary multipart.
  const res = await apiFetch(
    `${API_URL}/admin/message-templates/import`,
    { method: "POST", body },
    navigate,
  );
  return readJson<MessageTemplate[]>(res);
}
